import { jsx } from "react/jsx-runtime";
import { createContext, useState, useMemo, useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ServerSentResourceType, CallTaskState } from "../../call-management.types.js";
import { prioritizeCalls } from "../../helpers/prioritizeCalls.js";
import { updateOrAddElement } from "../../helpers/updateElement.js";
import { useCallTasks } from "../../hooks/useCallTasks.js";
import { useServerSentEvent } from "../../hooks/useServerSentEvents.js";
import "date-fns";
import "../../call-management.constants.js";
import "../AxiosProvider/index.js";
import "axios";
import "../../helpers/withQueryParams.js";
const CallTasksContext = createContext(void 0);
const canShowTask = (callTask, showAnonymous = true, showIncomingCalls = true) => {
  const isCallRingingOngoing = [CallTaskState.Open_InProgress, CallTaskState.Open_Ringing].includes(
    (callTask == null ? void 0 : callTask.currentState) ?? CallTaskState.Done
  );
  return !!(callTask == null ? void 0 : callTask.caller) && !isCallRingingOngoing || showIncomingCalls && isCallRingingOngoing && !!(callTask == null ? void 0 : callTask.caller) || showAnonymous && !(callTask == null ? void 0 : callTask.caller) && (isCallRingingOngoing || (callTask == null ? void 0 : callTask.hasAnsweringMachineRecordings) && (callTask == null ? void 0 : callTask.currentState));
};
const isRinging = (callTask) => callTask.currentState === CallTaskState.Open_Ringing;
const isOngoing = (callTask) => callTask.currentState === CallTaskState.Open_InProgress;
const CallTasksProvider = ({
  children,
  auth,
  baseURL,
  clinicReferenceId,
  showAnonymous,
  showIncomingCalls
}) => {
  const [loadSolvedCalls, setLoadSolvedCalls] = useState(false);
  const queryClient = useQueryClient();
  const { data: open, queryKey: openQueryKey } = useCallTasks({
    key: "open-call-tasks",
    auth,
    baseURL,
    clinicReferenceId
  });
  const { data: solved, queryKey: solvedQueryKey } = useCallTasks({
    key: "solved-call-tasks",
    auth,
    baseURL,
    clinicReferenceId,
    disabled: !loadSolvedCalls
  });
  const openCallsList = useMemo(
    () => open.filter((callTask) => canShowTask(callTask, showAnonymous, showIncomingCalls)),
    [open]
  );
  const solvedCallsList = useMemo(
    () => solved.filter((callTask) => canShowTask(callTask, showAnonymous, showIncomingCalls)),
    [solved]
  );
  const activeCallsList = useMemo(
    () => openCallsList.filter((callTask) => isRinging(callTask) || isOngoing(callTask)),
    [openCallsList]
  );
  const updateCallsLists = (updatedCallTask) => {
    if (!updatedCallTask)
      return;
    const solvedStates = [CallTaskState.Done, CallTaskState.Ignored, CallTaskState.Done_AvailyBooking];
    const isSolved = solvedStates.includes(updatedCallTask.currentState);
    const previousOpenCalls = queryClient.getQueryData(openQueryKey) || [];
    const previousSolvedCalls = queryClient.getQueryData(solvedQueryKey) || [];
    if (isSolved) {
      const filteredOpenCalls = previousOpenCalls.filter(
        ({ referenceId }) => updatedCallTask.referenceId !== referenceId
      );
      const updatedSolvedCalls = updateOrAddElement(previousSolvedCalls, updatedCallTask).filter(
        (callTask) => canShowTask(callTask, showAnonymous, showIncomingCalls)
      );
      queryClient.setQueryData(openQueryKey, filteredOpenCalls);
      queryClient.setQueryData(solvedQueryKey, updatedSolvedCalls);
    } else {
      const updatedOpenCalls = updateOrAddElement(previousOpenCalls, updatedCallTask).filter(
        (callTask) => canShowTask(callTask, showAnonymous, showIncomingCalls)
      );
      queryClient.setQueryData(openQueryKey, updatedOpenCalls);
    }
  };
  const calls = useMemo(
    () => ({
      open: prioritizeCalls(openCallsList),
      solved: solvedCallsList,
      active: activeCallsList,
      loadSolvedCalls,
      setLoadSolvedCalls,
      openQueryKey,
      solvedQueryKey
    }),
    [open, loadSolvedCalls, solved, openQueryKey, solvedQueryKey]
  );
  useServerSentEvent(ServerSentResourceType.CALL_TASK, "CREATED", updateCallsLists);
  useServerSentEvent(ServerSentResourceType.CALL_TASK, "UPDATED", updateCallsLists);
  return /* @__PURE__ */ jsx(CallTasksContext.Provider, { value: calls, children });
};
const useCallTasksProvider = () => {
  const context = useContext(CallTasksContext);
  if (context === void 0) {
    throw new Error("useCallTasksProvider must be used within a CallTasksProvider");
  }
  return context;
};
export {
  CallTasksProvider,
  useCallTasksProvider
};
